import { DataObject } from '@shared/models/data-object.model';
import { RoleTypes } from '@shared/models/role-types';

// To be moved to business rules library.

const BAD_COMPANY_NAMES = [
  'unknown',
  'research in progress',
  'na',
  'undisclosed',
  'confidential',
  'delete',
  'not provided',
];

enum RateValueOptions {
  Null = 'null',
  Zero = 'zero',
  GreaterThanZero = 'GreaterThanZero',
}
function correctTypos(name) {
  const typoCorrections = {
    unkown: 'unknown',
    na: 'na',
    undisclosed: 'undisclosed',
    confidential: 'confidential',
    delete: 'delete',
    'not provided': 'not provided',
    'research in progress': 'research in progress',
  };
  return typoCorrections[name] || name;
}

enum LesseeValueOptions {
  Null = 'null',
  ExactMatch = 'exactMatch',
  DoesNotContain = 'doesNotContain',
}

export enum LeaseRecordType {
  PartialLeaseComp = 'Partial Lease Comp',
  TenantNoComp = 'Tenant (No Comp)',
  LeaseComp = 'Lease Comp',
  Tenant = 'Owner Occupied',
}

function populateRateValue(baseRentValue: number): RateValueOptions {
  if (baseRentValue === null || baseRentValue === 0) {
    return baseRentValue === null ? RateValueOptions.Null : RateValueOptions.Zero;
  }
  return RateValueOptions.GreaterThanZero;
}

function getUniqueCompanyNames(
  companies: Array<{
    role_desc: string;
    party_organization_display_name: string;
    _mode: string;
  }>,
  role: RoleTypes
) {
  return (companies ?? [])
    .filter(({ role_desc, _mode }) => _mode !== 'delete' && role_desc === role)
    .map(company => company.party_organization_display_name)
    .filter((value, index, self) => self.indexOf(value) === index);
}

function populateLesseeValue(
  companies: Array<{
    role_desc: string;
    party_organization_display_name: string;
    _mode: string;
  }>
): LesseeValueOptions {
  const uniqueLesseeNames = getUniqueCompanyNames(companies, RoleTypes.LESSEE);
  const uniqueLessorNames = getUniqueCompanyNames(companies, RoleTypes.LESSOR);

  if (uniqueLesseeNames.some(lessee => uniqueLessorNames.includes(lessee))) {
    return LesseeValueOptions.ExactMatch;
  }

  if (
    !uniqueLesseeNames.length ||
    uniqueLesseeNames.some(name => !name || BAD_COMPANY_NAMES.includes(name.trim().toLowerCase()))
  ) {
    return LesseeValueOptions.Null;
  }

  return LesseeValueOptions.DoesNotContain;
}

const LEASE_RECORD_MAPPING_FIELD = Object.freeze({
  [`${LesseeValueOptions.ExactMatch}_${RateValueOptions.Null}`]: LeaseRecordType.Tenant,
  [`${LesseeValueOptions.ExactMatch}_${RateValueOptions.Zero}`]: LeaseRecordType.Tenant,
  [`${LesseeValueOptions.ExactMatch}_${RateValueOptions.GreaterThanZero}`]: LeaseRecordType.Tenant,
});

export function populateLeaseRecordType(record: DataObject): LeaseRecordType {
  const rateValue = populateRateValue(record.base_rent_yearly);
  const lesseeValue = populateLesseeValue(record.company_contact_role_addresses);

  if (isOwnerOccupied(lesseeValue)) {
    return LeaseRecordType.Tenant;
  }
  if (isTenantNoComp(record, lesseeValue, rateValue)) {
    return LeaseRecordType.TenantNoComp;
  }

  if (isLeaseComp(record, lesseeValue, rateValue)) {
    return LeaseRecordType.LeaseComp;
  }
  if (isPartialLeaseComp(lesseeValue, record, rateValue)) {
    return LeaseRecordType.PartialLeaseComp;
  }

  return LEASE_RECORD_MAPPING_FIELD[`${lesseeValue}_${rateValue}`];
}

function isOwnerOccupied(lesseeValue: LesseeValueOptions): boolean {
  return lesseeValue === LesseeValueOptions.ExactMatch;
}

function isTenantNoComp(record: DataObject, lesseeValue: LesseeValueOptions, rateValue: RateValueOptions): boolean {
  const hasNoDates = !record.arrangement_signed_date && !record.arrangement_start_date && !record.occupancy_date;
  const uniqueLesseeNames = getUniqueCompanyNames(record.company_contact_role_addresses, RoleTypes.LESSEE);
  const containsBadCompanyName = uniqueLesseeNames.some(companyName =>
    BAD_COMPANY_NAMES.includes(correctTypos(companyName?.trim?.().toLowerCase()))
  );
  const hasRateValue = rateValue === RateValueOptions.GreaterThanZero;

  return (
    (hasNoDates &&
      lesseeValue !== LesseeValueOptions.ExactMatch &&
      hasRateValue &&
      !containsBadCompanyName &&
      lesseeValue !== LesseeValueOptions.Null) ||
    (hasNoDates &&
      lesseeValue !== LesseeValueOptions.ExactMatch &&
      !hasRateValue &&
      !containsBadCompanyName &&
      lesseeValue !== LesseeValueOptions.Null) ||
    (hasNoDates &&
      lesseeValue !== LesseeValueOptions.ExactMatch &&
      hasRateValue &&
      containsBadCompanyName &&
      lesseeValue !== LesseeValueOptions.Null)
  );
}

function isLeaseComp(record: DataObject, lesseeValue: LesseeValueOptions, rateValue: RateValueOptions): boolean {
  const hasSignedDate = !!record.arrangement_signed_date;
  const hasStartDate = !!record.arrangement_start_date;
  const hasOccupancyDate = !!record.occupancy_date;
  const hasRateValue = rateValue === RateValueOptions.GreaterThanZero;
  const uniqueLesseeNames = getUniqueCompanyNames(record.company_contact_role_addresses, RoleTypes.LESSEE);
  const containsBadCompanyName = uniqueLesseeNames.some(companyName =>
    BAD_COMPANY_NAMES.includes(correctTypos(companyName?.trim?.().toLowerCase()))
  );

  const hasRequiredDates =
    (hasSignedDate && (hasStartDate || hasOccupancyDate)) || (!hasStartDate && !hasOccupancyDate);
  const isValidLessee = lesseeValue !== LesseeValueOptions.ExactMatch && lesseeValue !== LesseeValueOptions.Null;
  const isValidRate = hasRateValue;
  const isValidCompany = !containsBadCompanyName;

  return isValidLessee && hasRequiredDates && isValidRate && isValidCompany;
}

function isPartialLeaseComp(lesseeValue: LesseeValueOptions, record: DataObject, rateValue: RateValueOptions): boolean {
  if (lesseeValue === LesseeValueOptions.Null) return true;
  if (lesseeValue === LesseeValueOptions.ExactMatch) return false;

  const hasSignedDate = !!record.arrangement_signed_date;
  const hasStartDate = !!record.arrangement_start_date;
  const hasOccupancyDate = !!record.occupancy_date;
  const hasRateValue = rateValue === RateValueOptions.GreaterThanZero;
  const uniqueLesseeNames = getUniqueCompanyNames(record.company_contact_role_addresses, RoleTypes.LESSEE);
  const containsBadCompanyName = uniqueLesseeNames.some(companyName =>
    BAD_COMPANY_NAMES.includes(correctTypos(companyName?.trim?.().toLowerCase()))
  );

  const missingDate = !hasSignedDate || !hasStartDate || !hasOccupancyDate;
  const missingRate = !hasRateValue;
  const commonCondition = !hasStartDate && hasOccupancyDate && hasSignedDate && hasRateValue;

  const conditions = [
    commonCondition,
    hasStartDate && !hasOccupancyDate && hasSignedDate && hasRateValue,
    !hasStartDate && !hasOccupancyDate && hasSignedDate && hasRateValue,
    hasStartDate && hasOccupancyDate && !hasSignedDate && hasRateValue,
    !hasStartDate && hasOccupancyDate && !hasSignedDate && hasRateValue,
    hasStartDate && !hasOccupancyDate && !hasSignedDate && hasRateValue,
    hasStartDate && hasOccupancyDate && hasSignedDate && !hasRateValue,
    !hasStartDate && hasOccupancyDate && hasSignedDate && !hasRateValue,
    hasStartDate && !hasOccupancyDate && hasSignedDate && !hasRateValue,
    !hasStartDate && !hasOccupancyDate && hasSignedDate && !hasRateValue,
    hasStartDate && hasOccupancyDate && !hasSignedDate && !hasRateValue,
    hasStartDate && !hasOccupancyDate && !hasSignedDate && !hasRateValue,
    hasStartDate && hasOccupancyDate && hasSignedDate && hasRateValue,
  ];

  return (
    ((missingDate || missingRate) && !containsBadCompanyName) ||
    (containsBadCompanyName ? conditions.some(condition => condition) : commonCondition)
  );
}
